/**
 * TimelineTrace — persistent vertical narration timeline.
 *
 * Replaces the previous ThinkingTrace which tail-truncated and made
 * older narration disappear. Each event in `activityLog` becomes a
 * timeline node with a colored dot + a natural-language line; the
 * timeline grows downward as the run unfolds and stays visible after
 * the answer arrives (the user can scroll back through what happened).
 *
 * Layout: 1px vertical guide line on the left, dots positioned on it,
 * text to the right. Max-height capped + autoscroll so it never
 * dominates the page; collapsed by default once the run is terminal.
 */
import { memo, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import type { MultiAgentState } from "@/stores/multiAgentStore";
import { humanizeTool } from "@/services/multi-agent";
import { TIMELINE_TONE_COLOR } from "@/constants/multi-agent/timeline.constants";

type Entry = MultiAgentState["activityLog"][number];

// Translate a raw activity entry into a human-friendly narration line.
// Falls back to the entry's stored text for kinds we don't specialize.
function narrate(e: Entry): string {
  if (e.tool) {
    const args = e.argsSummary ? ` (${e.argsSummary})` : "";
    const ms = typeof e.durationMs === "number" ? ` · ${e.durationMs}ms` : "";
    return `${e.tool}${args}${ms}`;
  }
  return e.text;
}

// One timeline node. Memoized so appending a new event only renders the new
// row — the existing (immutable) entries keep their rendered output. Entries are
// appended, never mutated, so reference-equality memo is correct here.
const TimelineRow = memo(function TimelineRow({ e }: { e: Entry }) {
  return (
    <li
      className="ma-fade-up flex items-start gap-3 pl-1"
      style={{ fontSize: 12, color: "var(--ma-text-muted)", lineHeight: 1.4 }}
      title={e.text}
    >
      {/* Dot anchored on the guide line */}
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: TIMELINE_TONE_COLOR[e.tone as keyof typeof TIMELINE_TONE_COLOR] ?? "var(--ma-text-muted)",
          flex: "none",
          marginTop: 5,
          marginLeft: 0,
          boxShadow: "0 0 0 3px var(--ma-surface)",
        }}
      />
      <span className="min-w-0 flex-1 line-clamp-2">
        {e.tool ? (
          <>
            <span style={{ fontFamily: "ui-monospace, monospace", color: "var(--ma-text)" }}>
              {humanizeTool(e.tool)}
            </span>
            {e.argsSummary && <span> ({e.argsSummary})</span>}
            {typeof e.durationMs === "number" && (
              <span style={{ marginLeft: 6, fontVariantNumeric: "tabular-nums" }}>
                {e.durationMs}ms
              </span>
            )}
            {!e.ok && e.resultSummary && (
              <span
                className="block truncate"
                style={{
                  marginTop: 2,
                  color: "var(--ma-state-err)",
                  fontFamily: "ui-sans-serif, system-ui, sans-serif",
                }}
              >
                {e.resultSummary}
              </span>
            )}
          </>
        ) : (
          narrate(e)
        )}
      </span>
    </li>
  );
});

export function TimelineTrace() {
  const activityLog = useMultiAgentStore((s) => s.activityLog);
  const status = useMultiAgentStore((s) => s.status);
  const isTerminal = status === "done" || status === "failed" || status === "cancelled";
  const [open, setOpen] = useState(true);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Autoscroll to the latest entry as new events arrive. Bottom-aligned
  // so the user sees the freshest narration without having to interact.
  useEffect(() => {
    if (!bodyRef.current) return;
    bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [activityLog.length]);

  if (activityLog.length === 0) return null;

  const phase = isTerminal
    ? (status === "done" ? "Run complete" : status === "failed" ? "Run failed" : "Run cancelled")
    : status === "planning" ? "Planning"
    : status === "starting" ? "Starting"
    : "Working";

  return (
    <div className="ma-tile ma-fade-up">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="ma-tile__title flex w-full items-center gap-2"
        style={{ background: "transparent", border: 0, padding: 0, marginBottom: 8, cursor: "pointer" }}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Timeline · {activityLog.length}
        <span
          className="ma-micro ml-auto"
          style={{ textTransform: "none", letterSpacing: 0, color: "var(--ma-text-muted)" }}
        >
          {phase}
        </span>
      </button>

      {open && (
        <div
          ref={bodyRef}
          style={{
            position: "relative",
            maxHeight: 320,
            overflowY: "auto",
            paddingLeft: 8,
          }}
        >
          {/* Vertical guide line */}
          <span
            aria-hidden
            style={{
              position: "absolute",
              left: 12,
              top: 8,
              bottom: 8,
              width: 1,
              background: "var(--ma-border)",
            }}
          />
          <ul className="m-0 flex flex-col gap-2 p-0" style={{ listStyle: "none" }}>
            {/* Append-only log, so the positional index is a stable tiebreaker
                for entries that share an id (id falls back to 0 when the event
                carries no _event_id). Rows still skip re-render via memo on `e`. */}
            {activityLog.map((e, index) => (
              <TimelineRow key={`${e.id}-${index}`} e={e} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
